// Renders a Card descriptor (the port of outlook-demo/cards.js cardHTML).
// Action + result clicks are delegated up to App via callbacks.

import type { Card, CardAction, CardResult } from "../types";

interface Props {
  card: Card;
  onAction: (action: CardAction) => void;
  onResult: (result: CardResult) => void;
  onOpen: (url: string) => void;
}

/** Result dates arrive either as an ISO timestamp (real mailbox messages, so the
 *  server doesn't impose its own timezone — App Service runs UTC) or as a canned
 *  human string like "Yesterday" from the mock engine. Format the former in the
 *  user's locale, pass the latter through untouched. */
function formatResultDate(date: string): string {
  if (!date) return "";
  const t = Date.parse(date);
  if (Number.isNaN(t)) return date;
  return new Date(t).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function CardView({ card, onAction, onResult, onOpen }: Props) {
  return (
    <div className="card">
      <div className="card-accent" />
      <div className="card-pad">
        {card.title && (
          <div className="card-title">
            <span>{card.icon || "✦"}</span> {card.title}
          </div>
        )}
        {card.text && <div className="card-text" dangerouslySetInnerHTML={{ __html: card.text }} />}

        {card.facts && card.facts.length > 0 && (
          <dl className="card-facts">
            {card.facts.map(([k, v], i) => (
              <div key={i} style={{ display: "contents" }}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        )}

        {card.results && card.results.length > 0 && (
          <ul className="card-list">
            {card.results.map((r, i) => (
              <li key={i}>
                <button className="result-link" onClick={() => onResult(r)}>
                  <span className="rl-subj">✉️ {r.subject}</span>
                  <div className="rl-meta">
                    {formatResultDate(r.date)}, {r.snippet}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        {card.draftPreview && <div className="draft-preview">{card.draftPreview}</div>}

        {/* Citation is a real, clickable link. It used to be a model-written
            string rendered as plain text, so users saw raw `<a href=...>` /
            markdown markup instead of a link.

            Never rendered alongside the Sources block below. The backend stopped
            setting both on the same card, and this condition keeps that true even
            if it regresses — showing "According to HR-115" directly above a Sources
            list containing HR-115 attributes one document twice. */}
        {card.citation && !card.sources?.length && (
          <span className="citation">
            📄 According to{" "}
            {card.citation.url ? (
              <button type="button" className="citation-link" onClick={() => onOpen(card.citation!.url!)} title={`Open ${card.citation.title} in SharePoint`}>
                {card.citation.title}
                {card.citation.page ? ` (p. ${card.citation.page})` : ""}
              </button>
            ) : (
              <span>
                {card.citation.title}
                {card.citation.page ? ` (p. ${card.citation.page})` : ""}
              </span>
            )}
          </span>
        )}

        {card.sources && card.sources.length > 0 && (
          <div className="card-sources">
            <div className="card-sources-label">Sources</div>
            {card.sources.map((s, i) => (
              <button key={i} className="source-row" onClick={() => onOpen(s.url)} title={`Open ${s.title} in SharePoint`}>
                <span className="src-name">📄 {s.title}{s.page ? ` (p. ${s.page})` : ""}</span>
                <span className="src-open">Open ↗</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {card.actions && card.actions.length > 0 && (
        <div className="card-actions">
          {card.actions.map((a) => (
            <button key={a.id} className={`card-btn${a.primary ? " primary" : ""}`} onClick={() => onAction(a)}>
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
